import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  AlertTriangle, ArrowLeft, CalendarClock, CheckCircle2, ClipboardList, Clock, FileText, FlaskConical,
  Hourglass, Info, Pill, Receipt, Stethoscope, Timer, User, ArrowRightCircle, BedDouble, ShieldAlert, Activity,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/cn';
import { fetchVisitSummary, type SegmentKey, type VisitSummary } from './api';
import { patientDisplayName } from '@/lib/patientName';

/**
 * The "Visit complete" screen (VisitWorkflow's done stage, and its read-only
 * review of any visit, finished or not): the whole visit
 * on one page — who came and why, what was found, what the doctor decided,
 * what happens next, and how the time was spent. One read,
 * GET /api/visits/:visitId/summary (backend/utils/visitSummary.js), whose
 * timings are the same rules as the Dashboard's wait-time insights.
 */

const SEG_COLOR: Record<SegmentKey, string> = {
  intake: 'bg-tone-violet', vitals: 'bg-tone-green', assessment: 'bg-tone-blue',
  wait_doctor: 'bg-tone-orange', consultation: 'bg-tone-teal', exit: 'bg-tone-slate',
};
const OUTCOME_TONE: Record<string, string> = {
  closed: 'bg-success-soft text-success-ink',
  followup: 'bg-info-soft text-info-ink',
  referred: 'bg-warn-soft text-warn-ink',
  admitted: 'bg-tone-violet-soft text-tone-violet',
};

const fmtMin = (m: number | null | undefined) =>
  m == null ? '—' : m < 1 ? '<1m' : m < 60 ? `${Math.round(m)}m` : `${Math.floor(m / 60)}h ${Math.round(m % 60)}m`;
const timeOf = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '—');
const dateOf = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const dayTime = (iso: string | null | undefined, ref?: string) =>
  !iso ? '—' : (ref && new Date(iso).toDateString() !== new Date(ref).toDateString() ? `${new Date(iso).toLocaleDateString([], { day: 'numeric', month: 'short' })}, ${timeOf(iso)}` : timeOf(iso));
const rupees = (n: number) => `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtDate = (d: string) => (/^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(`${d}T00:00:00`).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : d);

/** A width share set through a ref (features/** bans inline style). */
function Grow({ grow, className, title, children }: { grow: number; className: string; title?: string; children?: ReactNode }) {
  const ref = useCallback((el: HTMLDivElement | null) => { el?.style.setProperty('flex-grow', String(grow)); }, [grow]);
  return <div ref={ref} title={title} className={cn('basis-0', className)}>{children}</div>;
}

function Panel({ icon, title, children, className, action }: { icon: ReactNode; title: string; children: ReactNode; className?: string; action?: ReactNode }) {
  return (
    <section className={cn('rounded-xl border border-line bg-card p-4 shadow-card', className)}>
      <div className="mb-3 flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-muted text-ink-muted">{icon}</span>
        <h3 className="text-[13.5px] font-semibold text-ink">{title}</h3>
        {action && <div className="ml-auto">{action}</div>}
      </div>
      {children}
    </section>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return <div className="text-[12.5px] italic text-ink-muted">{children}</div>;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mb-3 last:mb-0">
      <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">{label}</div>
      {children}
    </div>
  );
}

function TimeTile({ icon, label, minutes, hint, tone, note }: { icon: ReactNode; label: string; minutes: number | null; hint: string; tone: string; note?: string | null }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-line bg-card p-3.5 shadow-card">
      <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', tone)}>{icon}</span>
      <div className="min-w-0">
        <div className="text-[11.5px] font-semibold text-ink-muted">{label}</div>
        <div className="text-[21px] font-bold leading-tight text-ink">{fmtMin(minutes)}</div>
        <div className="text-[11px] text-ink-muted">{hint}</div>
        {note && <div className="mt-1 inline-block rounded bg-warn-soft px-1.5 text-[10.5px] font-semibold text-warn-ink">{note}</div>}
      </div>
    </div>
  );
}

const HIGHLIGHT_STYLE = {
  warn: { icon: <AlertTriangle className="h-4 w-4" />, cls: 'bg-warn-soft text-warn-ink' },
  info: { icon: <Info className="h-4 w-4" />, cls: 'bg-info-soft text-info-ink' },
  action: { icon: <ArrowRightCircle className="h-4 w-4" />, cls: 'bg-tone-teal-soft text-tone-teal' },
};

interface Props {
  visitCode: string;
  onBackToList: () => void;
  /** Label of the button that calls onBackToList. */
  backLabel?: string;
  onViewPatientRecord?: (uhid: string) => void;
}

export function VisitCompleteSummary({ visitCode, onBackToList, backLabel = 'Back to visit list', onViewPatientRecord }: Props) {
  const [data, setData] = useState<VisitSummary | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setData(null); setError('');
    fetchVisitSummary(visitCode)
      .then(d => { if (alive) setData(d); })
      .catch(e => { if (alive) setError(e instanceof Error ? e.message : String(e)); });
    return () => { alive = false; };
  }, [visitCode]);

  if (error) {
    return (
      <div className="mx-auto max-w-[640px] rounded-xl border border-line bg-card p-6 text-center">
        <CheckCircle2 className="mx-auto h-10 w-10 text-success" />
        <div className="mt-2 text-[16px] font-bold text-ink">Visit complete</div>
        <div className="mt-1 text-[12.5px] text-ink-muted">{visitCode} · The summary could not be loaded: {error}</div>
        <Button className="mt-4" onClick={onBackToList}>← {backLabel}</Button>
      </div>
    );
  }
  if (!data) return <div className="p-6 text-[13px] text-ink-muted">Loading visit summary…</div>;

  const { visit, patient, care, timing, timeline } = data;
  const outcomeKey = visit.resolution || 'closed';
  // Read-only review can show a visit that is still in progress (insights page).
  const done = visit.current_stage === 'done';
  const stageNow = timeline.find(t => t.stage === visit.current_stage)?.label ?? visit.current_stage.replace(/_/g, ' ');
  const leftOpen = (k: string) => timing.excluded.includes(k);
  // For the bar, a left-open stage is drawn at the cap so it cannot swamp the real stages.
  const barSegs = timing.segments.filter(s => s.minutes != null && s.minutes > 0);
  const primaryDx = data.diagnoses[0];

  return (
    <div className="fade-in mx-auto w-full max-w-[1400px] space-y-4">
      {/* ── Header ─────────────────────────────────────────── */}
      <section className="overflow-hidden rounded-xl border border-line bg-card shadow-card">
        <div className={cn('flex flex-wrap items-start gap-4 border-b border-line px-5 py-4', done ? 'bg-success-soft/50' : 'bg-info-soft/60')}>
          <span className={cn('flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white', done ? 'bg-success' : 'bg-tone-blue')}>
            {done ? <CheckCircle2 className="h-6 w-6" /> : <Activity className="h-6 w-6" />}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className={cn('text-[11px] font-semibold uppercase tracking-wide', done ? 'text-success-ink' : 'text-info-ink')}>
                {done ? 'Visit complete' : `Visit in progress · at ${stageNow}`}
              </span>
              {done && (
                <span className={cn('rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold', OUTCOME_TONE[outcomeKey] ?? OUTCOME_TONE.closed)}>
                  {visit.resolution_label}{visit.followup_date ? ` · ${fmtDate(visit.followup_date)}` : ''}
                </span>
              )}
              {visit.visit_type !== 'OP' && <Badge variant="info">{visit.visit_type}</Badge>}
              <Badge variant="neutral">{visit.is_new_patient ? 'New patient' : 'Returning patient'}</Badge>
            </div>
            <h2 className="mt-1 truncate text-[22px] font-bold text-ink">{patientDisplayName(patient)}</h2>
            <div className="mt-0.5 text-[12.5px] text-ink-muted">
              {[patient.uhid, [patient.age != null ? `${patient.age}y` : null, patient.gender].filter(Boolean).join(' '), patient.blood_group ? `Blood group ${patient.blood_group}` : null]
                .filter(Boolean).join(' · ')}
            </div>
            {primaryDx && (
              <div className="mt-2 text-[13px] text-ink">
                <span className="text-ink-muted">Diagnosis: </span><b>{primaryDx.diagnosis}</b>
                {data.diagnoses.length > 1 && <span className="text-ink-muted"> +{data.diagnoses.length - 1} more</span>}
              </div>
            )}
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-[12.5px] sm:grid-cols-4">
            <div><dt className="text-ink-muted">Doctor</dt><dd className="font-semibold text-ink">{care.doctor_name ? (/^dr\b/i.test(care.doctor_name) ? care.doctor_name : `Dr. ${care.doctor_name}`) : '—'}</dd></div>
            <div><dt className="text-ink-muted">Token</dt><dd className="font-semibold text-ink">{care.token ?? '—'}{care.appointment ? ' · Appt' : ' · Walk-in'}</dd></div>
            <div><dt className="text-ink-muted">Arrived</dt><dd className="font-semibold text-ink">{dateOf(visit.started_at)}, {timeOf(visit.started_at)}</dd></div>
            <div><dt className="text-ink-muted">Left</dt><dd className="font-semibold text-ink">{done ? dayTime(visit.resolved_at, visit.started_at) : 'Still here'}</dd></div>
          </dl>
        </div>
        <div className="flex flex-wrap items-center gap-2 px-5 py-2.5">
          <span className="font-mono text-[12px] text-ink-muted">{visit.visit_code}</span>
          <div className="ml-auto flex gap-2">
            {onViewPatientRecord && patient.uhid && (
              <Button variant="ghost" size="sm" onClick={() => onViewPatientRecord(patient.uhid)}><User className="mr-1 h-3.5 w-3.5" /> Patient record</Button>
            )}
            <Button size="sm" onClick={onBackToList}><ArrowLeft className="mr-1 h-3.5 w-3.5" /> {backLabel}</Button>
          </div>
        </div>
      </section>

      {/* ── Time at a glance ───────────────────────────────── */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <TimeTile icon={<Hourglass className="h-5 w-5" />} tone="bg-tone-violet-soft text-tone-violet" label="Intake → doctor"
          minutes={timing.intake_to_doctor_min} hint="From intake until the doctor started" />
        <TimeTile icon={<Clock className="h-5 w-5" />} tone="bg-tone-orange-soft text-tone-orange" label="Waiting for doctor"
          minutes={timing.wait_doctor_min} hint="In the queue after vitals / assessment" note={leftOpen('wait_doctor') ? 'left open' : null} />
        <TimeTile icon={<Stethoscope className="h-5 w-5" />} tone="bg-tone-teal-soft text-tone-teal" label="Consultation"
          minutes={timing.consultation_min} hint={timing.segments.find(x => x.key === 'consultation')?.state === 'open' ? 'In progress' : 'Time with the doctor'} note={leftOpen('consultation') ? 'left open — not real time' : null} />
        <TimeTile icon={<Timer className="h-5 w-5" />} tone="bg-tone-blue-soft text-tone-blue" label="Total visit"
          minutes={timing.total_visit_min} hint={done ? 'Intake to leaving' : 'So far'} note={leftOpen('total_visit') ? 'left open' : null} />
      </div>

      {/* ── Journey ────────────────────────────────────────── */}
      <Panel icon={<Activity className="h-4 w-4" />} title="Journey through the hospital">
        {barSegs.length > 0 ? (
          <>
            <div className="flex h-9 w-full gap-0.5 overflow-hidden rounded-lg">
              {barSegs.map(s => (leftOpen(s.key) ? (
                // A stage left open is not real time: a fixed, faded block, so it
                // cannot swamp the stages that were actually timed.
                <div key={s.key} title={`${s.label}: ${fmtMin(s.minutes)} — left open, not real time`}
                  className={cn('flex w-28 flex-none items-center justify-center overflow-hidden text-[10.5px] font-semibold text-white opacity-45', SEG_COLOR[s.key])}>
                  <span className="truncate px-1">{fmtMin(s.minutes)} · left open</span>
                </div>
              ) : (
                <Grow key={s.key} grow={Math.max(s.minutes ?? 0, 0.5)} title={`${s.label}: ${fmtMin(s.minutes)}`}
                  className={cn('flex min-w-[34px] items-center justify-center overflow-hidden text-[10.5px] font-semibold text-white', SEG_COLOR[s.key])}>
                  <span className="truncate px-1">{fmtMin(s.minutes)}</span>
                </Grow>
              )))}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11.5px] text-ink-muted">
              {timing.segments.map(s => (
                <span key={s.key} className={cn('inline-flex items-center gap-1.5', s.state === 'skipped' && 'opacity-50')}>
                  <span className={cn('h-2.5 w-2.5 rounded-sm', SEG_COLOR[s.key])} />
                  {s.label}{s.state === 'skipped' ? ' — skipped' : s.state === 'open' ? ' — in progress' : ''}
                  {timing.longest_wait?.key === s.key && <span className="rounded bg-tone-orange-soft px-1 font-semibold text-tone-orange">longest wait</span>}
                </span>
              ))}
            </div>
          </>
        ) : <Empty>No stage timings were recorded for this visit.</Empty>}

        {timeline.length > 0 && (
          <ol className="mt-4 grid list-none grid-cols-2 pl-0 gap-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-8">
            {timeline.map((t, i) => (
              <li key={t.stage} className="relative rounded-lg border border-line bg-muted/40 px-2.5 py-2">
                <div className="text-[10.5px] font-semibold text-ink-muted">{i + 1}. {t.label}</div>
                <div className="text-[12.5px] font-semibold text-ink">{dayTime(t.activated_at, visit.started_at)}</div>
                <div className={cn('text-[11px]', t.minutes != null && t.minutes > timing.cap_minutes ? 'font-semibold text-warn-ink' : 'text-ink-muted')}>
                  {t.minutes != null ? fmtMin(t.minutes) : 'open'}{t.completed_by ? ` · ${t.completed_by}` : ''}
                </div>
              </li>
            ))}
          </ol>
        )}

        {data.highlights.length > 0 && (
          <div className="mt-4">
            <div className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">What to notice</div>
            <ul className="grid grid-cols-1 gap-2 lg:grid-cols-2">
              {data.highlights.map((h, i) => (
                <li key={i} className={cn('flex items-start gap-2 rounded-lg px-3 py-2 text-[12.5px]', HIGHLIGHT_STYLE[h.tone].cls)}>
                  <span className="mt-0.5 shrink-0">{HIGHLIGHT_STYLE[h.tone].icon}</span>{h.text}
                </li>
              ))}
            </ul>
          </div>
        )}
        {data.rollbacks.length > 0 && (
          <div className="mt-3 text-[12px] text-ink-muted">
            {data.rollbacks.map((r, i) => (
              <div key={i}>↩ Moved back from <b>{r.from_label}</b> to <b>{r.to_label}</b> at {dayTime(r.done_at, visit.started_at)}{r.done_by ? ` by ${r.done_by}` : ''} — “{r.reason}”</div>
            ))}
          </div>
        )}
      </Panel>

      {/* ── Findings | Decision ────────────────────────────── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Panel icon={<ClipboardList className="h-4 w-4" />} title="Why they came & what was found">
          <Field label="Complaints">
            {data.presentation.complaints.length ? (
              <div className="flex flex-wrap gap-1.5">
                {data.presentation.complaints.flatMap(c => c.split(/\s*,\s*/)).filter(Boolean).map((c, i) => (
                  <span key={i} className="rounded-full bg-muted px-2.5 py-0.5 text-[12px] text-ink">{c}</span>
                ))}
              </div>
            ) : <Empty>None recorded</Empty>}
          </Field>
          {(data.presentation.ailment || data.presentation.risk_level || data.presentation.pain_scale) && (
            <Field label="Triage">
              <div className="flex flex-wrap gap-1.5 text-[12px]">
                {data.presentation.ailment && <Badge variant="neutral">{data.presentation.ailment}</Badge>}
                {data.presentation.risk_level && <Badge variant={/high/i.test(data.presentation.risk_level) ? 'danger' : /medium/i.test(data.presentation.risk_level) ? 'warn' : 'success'}>Risk: {data.presentation.risk_level}</Badge>}
                {data.presentation.pain_scale && <Badge variant="neutral">Pain {data.presentation.pain_scale}/10</Badge>}
              </div>
            </Field>
          )}
          <Field label="Vitals">
            {data.vitals.length ? (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {data.vitals.map(v => (
                  <div key={v.key} className={cn('rounded-lg border px-2.5 py-1.5', v.flag ? 'border-danger/30 bg-danger-soft' : 'border-line bg-muted/30')}>
                    <div className="text-[10.5px] text-ink-muted">{v.label}</div>
                    <div className={cn('text-[15px] font-bold', v.flag ? 'text-danger-ink' : 'text-ink')}>
                      {v.value}<span className="ml-0.5 text-[11px] font-normal text-ink-muted">{v.unit}</span>
                      {v.flag && <span className="ml-1 text-[11px]">{v.flag === 'high' ? '↑' : '↓'}</span>}
                    </div>
                  </div>
                ))}
              </div>
            ) : <Empty>No vitals recorded</Empty>}
          </Field>
          <Field label="Allergies">
            {data.allergies.length ? (
              <div className="flex flex-wrap gap-1.5">
                {data.allergies.map((a, i) => (
                  <span key={i} className="inline-flex items-center gap-1 rounded-full bg-danger-soft px-2.5 py-0.5 text-[12px] font-semibold text-danger-ink">
                    <ShieldAlert className="h-3 w-3" />{a.substance}{a.reaction ? ` — ${a.reaction}` : ''}
                  </span>
                ))}
              </div>
            ) : <Empty>No known allergies recorded</Empty>}
          </Field>
          {(data.presentation.current_meds || data.presentation.nurse_notes) && (
            <Field label="Notes from the nurse">
              <div className="text-[12.5px] text-ink">
                {data.presentation.current_meds && <div>Current medicines: {data.presentation.current_meds}</div>}
                {data.presentation.nurse_notes && <div>{data.presentation.nurse_notes}</div>}
              </div>
            </Field>
          )}
        </Panel>

        <Panel icon={<Stethoscope className="h-4 w-4" />} title="What the doctor decided">
          <Field label="Diagnosis">
            {data.diagnoses.length ? (
              <ol className="list-none space-y-1 pl-0">
                {data.diagnoses.map((d, i) => (
                  <li key={i} className="flex items-center gap-2 text-[13px] text-ink">
                    <span className={cn('flex h-5 w-5 items-center justify-center rounded-full text-[10.5px] font-bold', i === 0 ? 'bg-tone-teal text-white' : 'bg-muted text-ink-muted')}>{i + 1}</span>
                    <span className={i === 0 ? 'font-semibold' : ''}>{d.diagnosis}</span>
                    {d.code && <span className="font-mono text-[11px] text-ink-muted">{d.code}</span>}
                  </li>
                ))}
              </ol>
            ) : <Empty>No structured diagnosis recorded — see the consultation notes</Empty>}
          </Field>
          <Field label="Prescription">
            {data.prescription.length ? (
              <ul className="divide-y divide-line rounded-lg border border-line">
                {data.prescription.map((p, i) => (
                  <li key={i} className="flex items-start gap-2.5 px-3 py-2">
                    <Pill className="mt-0.5 h-4 w-4 shrink-0 text-tone-teal" />
                    <div className="min-w-0 text-[12.5px]">
                      <div className="font-semibold text-ink">{p.drug}{p.strength ? ` ${p.strength}` : ''}</div>
                      <div className="text-ink-muted">{[p.dose, p.route, p.frequency, p.duration].filter(Boolean).join(' · ') || '—'}</div>
                      {p.instructions && <div className="text-[11.5px] text-ink-muted">{p.instructions}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            ) : <Empty>No medicines prescribed</Empty>}
          </Field>
          <Field label="Investigations">
            {data.labs.length ? (
              <ul className="space-y-1.5">
                {data.labs.map(l => (
                  <li key={l.order_number} className="flex flex-wrap items-center gap-2 text-[12.5px]">
                    <FlaskConical className="h-4 w-4 text-tone-blue" />
                    <span className="text-ink">{l.tests.map(t => t.name).join(', ') || l.order_number}</span>
                    <Badge variant={l.status === 'REPORTED' ? 'success' : l.status === 'CANCELLED' ? 'neutral' : 'warn'}>
                      {l.status.replace(/_/g, ' ').toLowerCase()}
                    </Badge>
                  </li>
                ))}
              </ul>
            ) : <Empty>No lab tests ordered</Empty>}
          </Field>
          {data.admission && (
            <Field label="Admission">
              <div className="flex items-start gap-2 text-[12.5px] text-ink">
                <BedDouble className="mt-0.5 h-4 w-4 text-tone-violet" />
                <div>
                  Admitted {dayTime(data.admission.admitted_at, visit.started_at)}{data.admission.ip_number ? ` · IP ${data.admission.ip_number}` : ''}
                  {data.admission.reason_for_admission && <div className="text-ink-muted">{data.admission.reason_for_admission}</div>}
                </div>
              </div>
            </Field>
          )}
          <Field label="Documents filed">
            {data.documents.length ? (
              <div className="flex flex-wrap gap-1.5">
                {data.documents.map(d => (
                  <span key={d.id} className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-[12px] text-ink">
                    <FileText className="h-3 w-3" />{d.doc_type}{d.version && d.version > 1 ? ` v${d.version}` : ''}
                  </span>
                ))}
              </div>
            ) : <Empty>No approved documents</Empty>}
          </Field>
        </Panel>
      </div>

      {/* ── Next steps | Billing ───────────────────────────── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.4fr_1fr]">
        <Panel icon={<CalendarClock className="h-4 w-4" />} title="Next steps" className="border-tone-teal/30">
          <ul className="space-y-2 text-[13px] text-ink">
            {!done && (
              <li className="flex items-start gap-2">
                <Activity className="mt-0.5 h-4 w-4 shrink-0 text-tone-blue" />
                <span>Visit still in progress at <b>{stageNow}</b> — the outcome has not been decided yet.</span>
              </li>
            )}
            {done && <li className="flex items-start gap-2">
              <ArrowRightCircle className="mt-0.5 h-4 w-4 shrink-0 text-tone-teal" />
              <span>
                <b>{visit.resolution_label}</b>
                {visit.resolution === 'followup' && visit.followup_date && <> — come back on <b>{fmtDate(visit.followup_date)}</b></>}
                {visit.resolution === 'followup' && !visit.followup_date && <span className="text-ink-muted"> — no date was set</span>}
                {visit.resolution === 'admitted' && ' — care continues on the ward'}
                {visit.resolution === 'referred' && ' — patient referred to another facility'}
                {visit.resolution === 'closed' && <span className="text-ink-muted"> — no follow-up needed</span>}
              </span>
            </li>}
            {visit.resolution_notes && (
              <li className="flex items-start gap-2"><Info className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" /><span>{visit.resolution_notes}</span></li>
            )}
            {data.labs.filter(l => !['REPORTED', 'CANCELLED'].includes(l.status)).map(l => (
              <li key={l.order_number} className="flex items-start gap-2">
                <FlaskConical className="mt-0.5 h-4 w-4 shrink-0 text-tone-blue" />
                <span>Lab results pending: {l.tests.map(t => t.name).join(', ')}</span>
              </li>
            ))}
            {data.prescription.length > 0 && (
              <li className="flex items-start gap-2"><Pill className="mt-0.5 h-4 w-4 shrink-0 text-tone-teal" /><span>Take {data.prescription.length} prescribed medicine{data.prescription.length > 1 ? 's' : ''} as directed</span></li>
            )}
          </ul>
        </Panel>

        <Panel icon={<Receipt className="h-4 w-4" />} title="Billing"
          action={data.bills.length ? <span className="text-[13px] font-bold text-ink">{rupees(data.billed_total)}</span> : undefined}>
          {data.bills.length ? (
            <ul className="divide-y divide-line">
              {data.bills.map(b => (
                <li key={b.id} className="flex items-center gap-2 py-1.5 text-[12.5px]">
                  <span className="font-mono text-[11.5px] text-ink-muted">{b.bill_number}</span>
                  <span className="text-ink">{b.bill_type}</span>
                  <span className="ml-auto font-semibold text-ink">{rupees(b.total_amount)}</span>
                  <Badge variant={b.payment_status === 'Paid' ? 'success' : 'warn'}>{b.payment_status ?? 'Pending'}</Badge>
                </li>
              ))}
            </ul>
          ) : <Empty>No bills for this visit</Empty>}
        </Panel>
      </div>
    </div>
  );
}
